import { useMemo } from 'react';

import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import _ from 'lodash';

import type { SubUnitAdult, SubUnitYouth } from '@appTypes/esb';
import type {
  SubUnitAssignment,
  SubUnitInfo,
} from '@modules/roster-v2/models/rosterMember';
import {
  assignmentRef,
  subUnitKey,
} from '@modules/roster-v2/models/rosterMemberView';
import {
  useGetSubUnitAdultsQuery,
  useGetSubUnitYouthsQuery,
} from '@modules/rtk/esb-api';

const EMPTY_ADULTS: SubUnitAdult[] = [];
const EMPTY_YOUTHS: SubUnitYouth[] = [];

interface UseSubUnitEnrichmentOptions {
  skipAdults: boolean;
  skipYouths: boolean;
}

/** The den or patrol's `subUnitKey`, or its fields when it has no id. */
function assignmentKey(assignment: SubUnitAssignment): string {
  const ref = assignmentRef(assignment);
  if (ref) return subUnitKey(ref);

  const denType = assignment.denType?.trim();
  const denNumber = assignment.denNumber?.trim();
  if (denType || denNumber) return `den:${denType ?? ''}:${denNumber ?? ''}`;

  const patrolName = assignment.patrolName?.trim();
  if (patrolName) return `patrol:${patrolName}`;

  return 'unassigned';
}

/** Builds loaded sub-unit enrichment for every member returned by the APIs. */
export function buildSubUnitEnrichment(
  youths: SubUnitYouth[],
  adults: SubUnitAdult[],
): Record<string, SubUnitInfo> {
  return _.chain([...youths, ...adults])
    .groupBy('personGuid')
    .mapValues(members => ({
      assignments: _.chain(members)
        .flatMap(member => member.subUnitInfo)
        .keyBy(assignmentKey)
        // we get the array of sub-units
        .values()
        // convert to value (exit .chain from lodash)
        .value(),
    }))
    .value();
}

export function useSubUnitEnrichment(
  orgGuid: string,
  { skipAdults, skipYouths }: UseSubUnitEnrichmentOptions,
) {
  const shouldSkipYouths = skipYouths || !orgGuid;
  const shouldSkipAdults = skipAdults || !orgGuid;
  const youthQuery = useGetSubUnitYouthsQuery(
    { orgGuid },
    { skip: shouldSkipYouths },
  );
  const adultQuery = useGetSubUnitAdultsQuery(
    { orgGuid },
    { skip: shouldSkipAdults },
  );

  const youths = youthQuery.data?.members ?? EMPTY_YOUTHS;
  const adults = adultQuery.data?.members ?? EMPTY_ADULTS;
  const byGuid = useMemo(
    () => buildSubUnitEnrichment(youths, adults),
    [youths, adults],
  );

  return {
    byGuid,
    isYouthLoading: !shouldSkipYouths && youthQuery.isLoading,
    isAdultLoading: !shouldSkipAdults && adultQuery.isLoading,
    error: (youthQuery.error ?? adultQuery.error) as
      | FetchBaseQueryError
      | undefined,
    refetch: () => {
      if (!shouldSkipYouths) youthQuery.refetch();
      if (!shouldSkipAdults) adultQuery.refetch();
    },
  };
}
